import { FormikProvider, useFormik } from 'formik';
import { User } from 'lib/generated/graphql';
import { LINKED_IN_URL_REGEX, TWITTER_REGEX, URL_REGEX } from 'lib/regex';
import { forwardRef, useImperativeHandle } from 'react';
import { InputField, TextArea } from 'src/shared-components/Form';
import { toSentenceCase } from 'utils/common';
import * as Yup from 'yup';

interface ObjectType {
  website: string;
  twitter: string;
  linkedIn: string;
}

// TODO: This form is repetitive, it can be refactored with the one from onboarding. It only misses the name
// frontend/components/Profile/User/OnboardingCreator/FormStep1.tsx
const FormDetails = forwardRef(
  (
    {
      user,
      onFormSubmitted,
    }: { user: User; onFormSubmitted: (name: string, bio: string, socialLinks: Array<any>) => void },
    ref,
  ) => {
    const form = useFormik({
      initialValues: {
        name: user.name || '',
        bio: user.bio || '',
        socialLinks: {
          link: {
            website: user.socialLinks?.find(link => link.platformName === 'Website')?.link,
            twitter: user.socialLinks
              ?.find(link => link.platformName === 'Twitter')
              ?.link?.replace('https://twitter.com/', ''),
            linkedIn: user.socialLinks?.find(link => link.platformName === 'LinkedIn')?.link,
          },
        },
      },
      validationSchema: Yup.object().shape({
        name: Yup.string().required(),
        bio: Yup.string().max(user.bio?.length > 160 ? 240 : 160, 'Maximum number of characters is 160'),
        socialLinks: Yup.object().shape({
          link: Yup.object().shape({
            website: Yup.string().matches(URL_REGEX, 'Is this a valid URL?'),
            twitterUsername: Yup.string().matches(TWITTER_REGEX, 'Hey please only input your username without the @'),
            linkedInLink: Yup.string().matches(LINKED_IN_URL_REGEX, 'Hey is this a valid LinkedIn URL?'),
          }),
        }),
      }),
      onSubmit: values => {
        const socialLinks = [];

        for (const key in values.socialLinks.link) {
          if (values.socialLinks.link[key as keyof ObjectType]) {
            socialLinks.push({
              id: user.socialLinks?.find(socialLink => socialLink.platformName === toSentenceCase(key))?.id || null,
              platformName: toSentenceCase(key),
              link: values.socialLinks.link[key as keyof ObjectType],
            });
          }
        }

        onFormSubmitted(values.name, values.bio, socialLinks);
      },
    });

    useImperativeHandle(ref, () => ({
      submitForm() {
        form.submitForm();
      },
    }));

    return (
      <FormikProvider value={form}>
        <div data-testid="onboarding-creator-step1" className="grid grid-cols-6 gap-4">
          <div className="col-span-6 sm:col-span-6">
            <div className="mt-4">
              <InputField name="name" autoComplete="off" label="Name" placeholder="Your name" />
            </div>
            <div className="mt-4">
              <TextArea
                name="bio"
                autoComplete="off"
                label="1-2 sentence bio"
                placeholder="1-2 sentences about you, your mission, passions, previous wins, ect."
                rows={3}
              />
            </div>
            <div className="mt-4">
              <InputField
                name="socialLinks['link']['website']"
                autoComplete="off"
                label="link to portfolio / personal website"
                placeholder="https://www.my.design"
              />
            </div>
            <div className="mt-4">
              <InputField
                name="socialLinks['link']['twitter']"
                autoComplete="off"
                label="twitter handle"
                placeholder="username"
              />
            </div>
            <div>
              <InputField
                name="socialLinks['link']['linkedIn']"
                autoComplete="off"
                label="linkedin profile link"
                placeholder="https://www.linkedin.com/in/username"
              />
            </div>
          </div>
        </div>
      </FormikProvider>
    );
  },
);

export default FormDetails;
